import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

const API = window.location.origin;

const GestionDenarios = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [usuariosFiltrados, setUsuariosFiltrados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editandoUsuario, setEditandoUsuario] = useState(null);
  const [nuevaCantidad, setNuevaCantidad] = useState('');

  useEffect(() => {
    cargarUsuarios();
  }, []);

  useEffect(() => {
    // Filtrar usuarios por búsqueda
    if (searchTerm.trim() === '') {
      setUsuariosFiltrados(usuarios);
    } else {
      const filtered = usuarios.filter(usuario =>
        usuario.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        usuario.apellido?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        usuario.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      // Mantener el orden de mayor a menor denarios
      const filteredOrdenados = filtered.sort((a, b) => b.denarios_totales - a.denarios_totales);
      setUsuariosFiltrados(filteredOrdenados);
    }
  }, [searchTerm, usuarios]);

  const cargarUsuarios = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      
      // Obtener todos los usuarios con sus denarios (endpoint optimizado)
      const response = await axios.get(`${API}/api/admin/usuarios-con-denarios`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Ordenar de mayor a menor cantidad de denarios
      const usuariosOrdenados = response.data.sort((a, b) => b.denarios_totales - a.denarios_totales);
      
      setUsuarios(usuariosOrdenados);
      setUsuariosFiltrados(usuariosOrdenados);
    } catch (error) {
      console.error('Error cargando usuarios:', error);
      toast.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  const abrirModalEditar = (usuario) => {
    setEditandoUsuario(usuario);
    setNuevaCantidad(usuario.denarios_totales.toString());
  };

  const cerrarModal = () => {
    setEditandoUsuario(null);
    setNuevaCantidad('');
  };

  const actualizarDenarios = async () => {
    if (!editandoUsuario) return;
    
    const cantidad = parseInt(nuevaCantidad);
    if (isNaN(cantidad) || cantidad < 0) {
      toast.error('Ingresa una cantidad válida (0 o mayor)');
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      await axios.put(
        `${API}/api/admin/denarios/actualizar`,
        {
          usuario_id: editandoUsuario.id,
          nueva_cantidad: cantidad
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Denarios actualizados: ${editandoUsuario.nombre} ahora tiene ${cantidad} ₯`);
      cerrarModal();
      cargarUsuarios();
    } catch (error) {
      console.error('Error actualizando denarios:', error);
      toast.error(error.response?.data?.detail || 'Error al actualizar denarios');
    }
  };

  const resetearDenarios = async (usuario) => {
    if (!window.confirm(`¿Resetear denarios de ${usuario.nombre} ${usuario.apellido} a 0?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      await axios.put(
        `${API}/api/admin/denarios/actualizar`,
        {
          usuario_id: usuario.id,
          nueva_cantidad: 0
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Denarios reseteados: ${usuario.nombre} ahora tiene 0 ₯`);
      cargarUsuarios();
    } catch (error) {
      console.error('Error reseteando denarios:', error);
      toast.error(error.response?.data?.detail || 'Error al resetear denarios');
    }
  };

  const limpiarDuplicados = async () => {
    if (!window.confirm('⚠️ LIMPIAR DUPLICADOS\n\nEsto eliminará registros duplicados en la base de datos.\n\nSe mantendrá el registro con más denarios de cada usuario.\n\n¿Continuar?')) {
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${API}/api/admin/denarios/limpiar-duplicados`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`✅ Limpieza completada: ${response.data.usuarios_corregidos} usuarios corregidos, ${response.data.duplicados_eliminados} duplicados eliminados`, {
        autoClose: 5000
      });
      
      cargarUsuarios();
    } catch (error) {
      console.error('Error limpiando duplicados:', error);
      toast.error(error.response?.data?.detail || 'Error al limpiar duplicados');
    }
  };

  const getRolBadge = (rol) => {
    const badges = {
      admin: 'bg-red-100 text-red-700',
      profesor: 'bg-blue-100 text-blue-700',
      estudiante: 'bg-green-100 text-green-700',
      pastor: 'bg-purple-100 text-purple-700'
    };
    return badges[rol] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-8">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2 flex items-center">
          <span className="text-3xl sm:text-4xl mr-3">💰</span>
          Gestión de Denarios
        </h2>
        <p className="text-gray-600">
          Administra los denarios de todos los usuarios del sistema
        </p>
      </div>

      {/* Buscador */}
      <div className="mb-6">
        <div className="relative">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="🔍 Buscar por nombre, apellido o email..."
            className="w-full px-4 py-3 pl-10 border-2 border-gray-300 rounded-xl focus:border-indigo-600 focus:outline-none"
          />
          <i className="fas fa-search absolute left-3 top-4 text-gray-400"></i>
        </div>
      </div>

      {/* Botón de Limpieza de Duplicados */}
      <div className="mb-6 p-4 bg-yellow-50 border-2 border-yellow-300 rounded-xl">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <h3 className="text-lg font-bold text-gray-800 mb-1">
              🧹 Herramienta de Mantenimiento
            </h3>
            <p className="text-sm text-gray-600">
              Si ves inconsistencias en los denarios (diferentes valores en Dashboard, Clasificación y aquí), usa esta herramienta para limpiar registros duplicados en la base de datos.
            </p>
          </div>
          <button
            onClick={limpiarDuplicados}
            className="ml-4 px-6 py-3 bg-yellow-500 text-white rounded-xl font-bold hover:bg-yellow-600 transition-all shadow-md hover:shadow-lg whitespace-nowrap"
          >
            🧹 Limpiar Duplicados
          </button>
        </div>
      </div>

      {/* Lista de Usuarios con estilo de clasificación */}
      {loading ? (
        <div className="text-center py-12">
          <i className="fas fa-spinner fa-spin text-4xl text-indigo-600 mb-4"></i>
          <p className="text-gray-600">Cargando usuarios...</p>
        </div>
      ) : usuariosFiltrados.length === 0 ? (
        <div className="text-center py-12">
          <i className="fas fa-users-slash text-6xl text-gray-300 mb-4"></i>
          <p className="text-gray-600">No se encontraron usuarios</p>
        </div>
      ) : (
        <div className="space-y-3">
          {usuariosFiltrados.map((usuario, index) => (
            <div
              key={usuario.id}
              className="flex items-center gap-4 p-5 bg-gray-50 rounded-xl hover:bg-gray-100 transition-all hover:shadow-md"
            >
              {/* Posición */}
              <div className="text-2xl font-bold w-10 text-center text-gray-400">
                #{index + 1}
              </div>

              {/* Información del Usuario */}
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-1">
                  <p className="font-bold text-gray-900 text-lg">
                    {usuario.nombre} {usuario.apellido}
                  </p>
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getRolBadge(usuario.rol)}`}>
                    {usuario.rol}
                  </span>
                </div>
                <p className="text-sm text-gray-600">{usuario.email}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {usuario.nivel_actual || 'Sembrador'}
                </p>
              </div>

              {/* Denarios */}
              <div className="text-right mr-4">
                <p className="text-3xl font-bold text-indigo-600">
                  {usuario.denarios_totales} ₯
                </p>
              </div>

              {/* Botones de Acción */}
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => abrirModalEditar(usuario)}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all text-sm font-semibold shadow-md hover:shadow-lg"
                  title="Editar cantidad"
                >
                  ✏️ Editar
                </button>
                
                <button
                  onClick={() => resetearDenarios(usuario)}
                  className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all text-sm font-semibold shadow-md hover:shadow-lg"
                  title="Resetear a 0"
                >
                  🗑️ Reset
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Info Footer */}
      <div className="mt-6 p-4 bg-blue-50 border-2 border-blue-200 rounded-xl">
        <p className="text-sm text-blue-800">
          <i className="fas fa-info-circle mr-2"></i>
          <strong>Total de usuarios:</strong> {usuarios.length} | <strong>Mostrando:</strong> {usuariosFiltrados.length}
        </p>
      </div>

      {/* Modal de Edición */}
      {editandoUsuario && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full">
            <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-6 rounded-t-2xl">
              <h3 className="text-2xl font-bold text-white text-center">
                ✏️ Editar Denarios
              </h3>
            </div>

            <div className="p-6">
              <div className="mb-6">
                <div className="text-center mb-4">
                  <p className="text-lg font-semibold text-gray-800">
                    {editandoUsuario.nombre} {editandoUsuario.apellido}
                  </p>
                  <p className="text-sm text-gray-600">{editandoUsuario.email}</p>
                  <div className="mt-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getRolBadge(editandoUsuario.rol)}`}>
                      {editandoUsuario.rol}
                    </span>
                  </div>
                </div>

                <div className="bg-gray-100 rounded-xl p-4 mb-4">
                  <p className="text-sm text-gray-600 mb-1">Denarios Actuales:</p>
                  <p className="text-3xl font-bold text-gray-800">
                    {editandoUsuario.denarios_totales} ₯
                  </p>
                </div>

                <label className="block text-gray-700 font-semibold mb-2">
                  Nueva Cantidad de Denarios:
                </label>
                <input
                  type="number"
                  value={nuevaCantidad}
                  onChange={(e) => setNuevaCantidad(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-600 focus:outline-none text-center text-2xl font-bold"
                  placeholder="0"
                  min="0"
                  autoFocus
                />
                <p className="text-xs text-gray-500 mt-2 text-center">
                  Ingresa la cantidad exacta que tendrá el usuario (puede ser 0)
                </p>
              </div>

              <div className="flex gap-4">
                <button
                  onClick={cerrarModal}
                  className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-100 transition-all"
                >
                  Cancelar
                </button>
                <button
                  onClick={actualizarDenarios}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
                >
                  Actualizar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GestionDenarios;
